// ═══════════════════════════════════════════════════════════════════
// plazaProp3D.jsx — THE PLAZA'S BLENDER-BUILT FIXTURES, ONE LOADER   (caP'n, 2026-09-22)
// ═══════════════════════════════════════════════════════════════════
// caP'n: "don't stop until the plaza's completely replaced". Every fixture
// (portal arches, the bulletin board, Willy's bench, Sweet Treats, the pack
// kiosk, the workshop tent) is now a glb built in Blender off plaza_1.webp —
// builders in gamemaster/models/sculpt/builders/plaza_props_blender/ — and
// this file is how the Plaza wears them.
//
// ⭐ THE SHELL LAW STILL HOLDS: a prop is placed by plazaZones exactly as its
// painted JSX twin is (the same `place` maths), so MOVING A KIOSK IS STILL A
// plazaZones EDIT. Everything that moves or glows live — fire, lights, the
// vortex, flames, bead sway — stays JSX, passed in as `live`.
//
// The look is the fountain's: toon-shaded over a painted atlas (colour x
// baked AO), with the ink line. By NAME, from the glb:
//   <prop>_body   the painted stone/wood/cloth — toon + ink
//   glow_*        lit and unshaded (gems, flames, glowing runes)
//   glass_*       see-through
//   slot_<key>*   a surface the GAME paints, unlit (a sign, a sigil, a rune strip):
//                 skin.slots[key] is its texture
//   skin_<key>*   a surface in the game's own canvas but LIT and inked (the tent):
//                 skin.maps[key]
// THE FAIL-SAFE LAW: the painted JSX twin stands in while loading, on any
// failure, and whenever the sculpt gate trips.
// ═══════════════════════════════════════════════════════════════════
import { Component, Suspense, useMemo } from "react";
import { useLoader } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { usePaintedBodies } from "./sculptGate";

export const propUrl = (file) => `${process.env.PUBLIC_URL || ""}/models/${file}`;
const SELF_LIGHT = 0.22;          // the fountain's number, so they sit together
const INK_W = 0.012;

let RAMP = null;
const toonRamp = () => {
  if (RAMP) return RAMP;
  RAMP = new THREE.DataTexture(new Uint8Array([150, 150, 150, 255, 215, 215, 215, 255, 255, 255, 255, 255]), 3, 1);
  RAMP.minFilter = RAMP.magFilter = THREE.NearestFilter; RAMP.needsUpdate = true;
  return RAMP;
};
let INK = null;
const inkMat = () => {
  if (INK) return INK;
  INK = new THREE.MeshBasicMaterial({ color: 0x120e16, side: THREE.BackSide });
  INK.customProgramCacheKey = () => "plazaPropInk";
  INK.onBeforeCompile = (sh) => {
    sh.vertexShader = sh.vertexShader.replace("#include <begin_vertex>", `#include <begin_vertex>\n  transformed += normalize( normal ) * ${INK_W.toFixed(4)};`);
  };
  return INK;
};
// the ink hull needs SMOOTH normals or it splits open at every hard edge:
// weld by position only, then average — cached per source geometry
const HULLS = new Map();
function hullGeo(g) {
  let h = HULLS.get(g.uuid);
  if (h) return h;
  h = new THREE.BufferGeometry();
  h.setAttribute("position", g.attributes.position.clone());
  if (g.index) h.setIndex(g.index.clone());
  h = mergeVertices(h, 1e-4); h.computeVertexNormals();
  HULLS.set(g.uuid, h);
  return h;
}

// skin: { tint: "#hex" | null, tintMix: 0..1, shade: multiplier, slots: { key: Texture },
//         slotColor: { key: "#hex" } (a multiplier on that slot), maps: { key: Texture }, noInk: RegExp }
export function prepareProp(scene, skin = {}) {
  const root = scene.clone(true);
  const meshes = [];
  root.traverse((o) => { if (o.isMesh) meshes.push(o); });
  const byName = {};
  meshes.forEach((o) => {
    const m = o.material, name = o.name || "";
    byName[name] = o;
    if (m.map) m.map.colorSpace = THREE.SRGBColorSpace;
    let mat;
    if (/^slot_/.test(name)) {
      const key = name.slice(5).replace(/[_.]?\d+$/, "");
      let tex = skin.slots && skin.slots[key];
      // glTF UVs run top-down: a canvas painted for the primitives (flipY) is cloned,
      // never changed — the painted twin shares the cached original
      if (tex) { tex = tex.clone(); tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true; }
      const dim = skin.slotColor && skin.slotColor[key];            // a carved slot can sit darker than a paper one
      mat = new THREE.MeshBasicMaterial({ map: tex || null, color: tex ? (dim || 0xffffff) : m.color, transparent: true, toneMapped: false, side: THREE.DoubleSide });
    } else if (/^skin_/.test(name)) {
      // a surface drawn in the game's OWN painted canvas (skin.maps[key]) but lit
      // and inked like the body — the tent's patched canvas
      const key = name.slice(5).replace(/[_.]?\d+$/, "");
      let tex = skin.maps && skin.maps[key];
      if (tex) { tex = tex.clone(); tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.colorSpace = THREE.SRGBColorSpace; tex.needsUpdate = true; }
      const col = tex ? new THREE.Color(skin.mapTint || 0xffffff) : m.color.clone();
      mat = new THREE.MeshToonMaterial({ color: col, map: tex || null, gradientMap: toonRamp(), emissive: col.clone().multiplyScalar(SELF_LIGHT), emissiveMap: tex || null });
      o.castShadow = true;
      const ink = new THREE.Mesh(hullGeo(o.geometry), inkMat());
      ink.name = name + "_ink"; ink.position.copy(o.position); ink.quaternion.copy(o.quaternion); ink.scale.copy(o.scale);
      ink.raycast = () => {};
      o.parent.add(ink);
    } else if (/^glow/.test(name)) {
      mat = new THREE.MeshBasicMaterial({ color: m.color, toneMapped: false });
    } else if (/^glass/.test(name)) {
      mat = new THREE.MeshStandardMaterial({ color: m.color, transparent: true, opacity: 0.22, roughness: 0.05, depthWrite: false });
    } else {
      const col = m.color.clone();
      if (skin.tint) col.lerp(new THREE.Color(skin.tint), skin.tintMix ?? 0.4);
      if (skin.shade) col.multiplyScalar(skin.shade);
      mat = new THREE.MeshToonMaterial({
        color: col, map: m.map || null, gradientMap: toonRamp(),
        emissive: col.clone().multiplyScalar(SELF_LIGHT), emissiveMap: m.map || null,
      });
      o.castShadow = true;
      if (!(skin.noInk && skin.noInk.test(name))) {
        const ink = new THREE.Mesh(hullGeo(o.geometry), inkMat());
        ink.name = name + "_ink"; ink.position.copy(o.position); ink.quaternion.copy(o.quaternion); ink.scale.copy(o.scale);
        ink.raycast = () => {};
        o.parent.add(ink);
      }
    }
    o.receiveShadow = true;
    o.material = mat;
  });
  return { root, byName };
}

class GlbBoundary extends Component {
  constructor(p) { super(p); this.state = { bad: false }; }
  static getDerivedStateFromError() { return { bad: true }; }
  componentDidCatch(e) { console.warn(`[plaza] ${this.props.what} did not load — its painted twin stands in`, e); }
  render() { return this.state.bad ? this.props.fallback : this.props.children; }
}

// place(props) → { pos:[x,y,z], rot?:[x,y,z], scale?: n|[x,y,z], skin? } | null  (the painted twin's own maths)
// Live: a component drawn in the same local frame (lights, fire, flicker) — { ...props, parts, pl }
export function makeSculptedProp({ file, Fallback, place, Live, what }) {
  const url = propUrl(file);
  function Model(props) {
    const gltf = useLoader(GLTFLoader, url);
    const pl = place(props);
    const skinKey = pl && pl.skinKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const parts = useMemo(() => (pl ? prepareProp(gltf.scene, pl.skin || {}) : null), [gltf, skinKey]);
    if (!pl || !parts) return null;
    return (
      <group position={pl.pos} rotation={pl.rot || [0, 0, 0]} scale={pl.scale ?? 1}>
        <primitive object={parts.root} />
        {Live ? <Live {...props} parts={parts} pl={pl} /> : null}
      </group>
    );
  }
  function SculptedProp(props) {
    const painted = usePaintedBodies();
    const fb = <Fallback {...props} />;
    if (painted) return fb;
    return (
      <GlbBoundary fallback={fb} what={what || file}>
        <Suspense fallback={fb}><Model {...props} /></Suspense>
      </GlbBoundary>
    );
  }
  SculptedProp.displayName = `Sculpted(${what || file})`;
  return SculptedProp;
}
